import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BarChart3, Calendar, Loader2, Download } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { API } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";
import { downloadMonthlyReportPdf } from "../lib/monthlyReportPdf";

const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function prevMonth(y, m) { if (m === 1) return [y - 1, 12]; return [y, m - 1]; }

export default function MonthlyReportCard() {
  const { t, i18n } = useTranslation();
  const fmtXof = useFmtXof();
  const isEn = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en");
  const MONTHS = isEn ? MONTHS_EN : MONTHS_FR;

  const today = new Date();
  const [defaultY, defaultM] = prevMonth(today.getFullYear(), today.getMonth() + 1);
  const [year, setYear] = useState(defaultY);
  const [month, setMonth] = useState(defaultM);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const periodLabel = useMemo(() => `${MONTHS[month - 1]} ${year}`, [year, month, MONTHS]);

  const monthOptions = useMemo(() => {
    const opts = [];
    let [y, m] = [today.getFullYear(), today.getMonth() + 1];
    for (let i = 0; i < 12; i++) {
      opts.push({ y, m, label: `${MONTHS[m - 1]} ${y}` });
      [y, m] = prevMonth(y, m);
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MONTHS]);

  const load = async (y, m) => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/reports/monthly`, { params: { year: y, month: m } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("monthlyReport.errorLoad"));
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reports/monthly`, { params: { year, month } });
        if (active) { setData(data); setError(""); }
      } catch (e) {
        if (active) { setError(e?.response?.data?.detail || t("monthlyReport.errorLoad")); setData(null); }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [year, month, t]);

  return (
    <Card className="mt-10 p-6 border-[#E5EBE7]" data-testid="parent-monthly-report-card">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#16A34A]" /> {t("monthlyReport.title")}
          </h2>
          <p className="text-sm text-[#4B5563] mt-1">{t("monthlyReport.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#16A34A]" />
          <select
            data-testid="monthly-report-period-select"
            value={`${year}-${month}`}
            onChange={(e) => { const [y, m] = e.target.value.split("-").map(Number); setYear(y); setMonth(m); }}
            className="rounded-full border border-[#E5EBE7] bg-white px-4 py-2 text-sm text-[#0A2518] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 capitalize"
          >
            {monthOptions.map((o) => (
              <option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`} className="capitalize">{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#4B5563] py-8">
          <Loader2 className="h-4 w-4 animate-spin text-[#16A34A]" /> {t("common.loading")}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 p-4 text-sm" data-testid="monthly-report-error">{error}</div>
      )}

      {!loading && data && (
        <div data-testid="monthly-report-content">
          <div className="text-xs uppercase tracking-wider text-[#15803D] mb-2 capitalize">{periodLabel}</div>

          <div className="grid sm:grid-cols-3 gap-3" data-testid="monthly-report-kpis">
            <div className="rounded-xl bg-[#F0FDF4] border border-[#16A34A]/30 p-4">
              <div className="text-[11px] uppercase tracking-wider text-[#15803D]">{t("monthlyReport.kpiHours")}</div>
              <div className="text-2xl font-display font-bold text-[#0A2518]">{(data.totals?.hours || 0).toFixed(1)} h</div>
            </div>
            <div className="rounded-xl bg-[#F0FDF4] border border-[#16A34A]/30 p-4">
              <div className="text-[11px] uppercase tracking-wider text-[#15803D]">{t("monthlyReport.kpiSessions")}</div>
              <div className="text-2xl font-display font-bold text-[#0A2518]">{data.totals?.sessions_count || 0}</div>
            </div>
            <div className="rounded-xl bg-[#F0FDF4] border border-[#16A34A]/30 p-4">
              <div className="text-[11px] uppercase tracking-wider text-[#15803D]">{t("monthlyReport.kpiAmount")}</div>
              <div className="text-2xl font-display font-bold text-[#0A2518]">{fmtXof(data.totals?.amount_xof)}</div>
            </div>
          </div>

          {data.totals?.sessions_count === 0 ? (
            <p className="mt-6 text-sm text-[#4B5563]">{t("monthlyReport.empty")}</p>
          ) : (
            <div className="mt-6 grid lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-[#0A2518] mb-2">{t("monthlyReport.byEducator")}</h3>
                <div className="space-y-2" data-testid="monthly-report-by-educator">
                  {(data.by_educator || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-[#E5EBE7] px-3 py-2 text-sm">
                      <span className="text-[#0A2518] truncate pr-2">{e.educator_name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="border-[#16A34A]/30 text-[#15803D]">{e.hours.toFixed(1)} h</Badge>
                        <span className="text-xs text-[#4B5563]">{fmtXof(e.amount_xof)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0A2518] mb-2">{t("monthlyReport.bySubject")}</h3>
                <div className="space-y-2" data-testid="monthly-report-by-subject">
                  {(data.by_subject || []).map((s, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-[#E5EBE7] px-3 py-2 text-sm">
                      <span className="text-[#0A2518] truncate pr-2">{s.subject}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="border-[#16A34A]/30 text-[#15803D]">{s.hours.toFixed(1)} h</Badge>
                        <span className="text-xs text-[#4B5563]">{t("monthlyReport.sessionsLabel", { count: s.sessions })}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-end gap-2 flex-wrap">
            <Button variant="outline" size="sm" data-testid="monthly-report-refresh-btn" onClick={() => load(year, month)} className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]">
              {t("common.refresh")}
            </Button>
            <Button size="sm" data-testid="monthly-report-download-pdf-btn" onClick={() => downloadMonthlyReportPdf(data)} disabled={!data || (data?.totals?.sessions_count || 0) === 0} className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white disabled:opacity-50">
              <Download className="h-4 w-4 mr-2" /> {t("monthlyReport.downloadPdf")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
